import React from "react";
import "./_objective-details.scss";

// ═══════════════════════════════════════════════════════════════════════════
// ObjectiveDetails — an Objective's Key Results and its owner, one design.
//
// ⚠️ BYTE-IDENTICAL IN BOTH REPOS. `diff` is the test:
//
//   10x-frontend/.../UserPanel/AnnualOkr/_shared/ObjectiveDetails.js
//   10x-coach-frontend/.../CoachPanel/AnnualOkr/_shared/ObjectiveDetails.js
//
// Four screens show the same two facts about an Objective — the CEO's coach
// remarks, the coach's review, and the live review on both sides — and until
// 2026-09-24 each drew them differently: a numbered <ol> here, "1." in bold
// there, a plain "Owner" label on one and nothing at all on another. The same
// Objective looked like three different objects depending on who was looking
// and when. This is that one design, so a change lands everywhere at once.
//
// ⚠️ PRESENTATIONAL ONLY. It takes already-normalised props rather than a raw
// payload, because the four callers get their data from four different
// endpoints (§4.4 + §4.6 joined, §6.3, and the §7 socket) whose field names do
// not agree. Normalising at each call site keeps the shape mismatch at the
// boundary instead of putting a payload-sniffing branch in a view component.
//
// ⚠️ NO `target`, ANYWHERE. It appears on exactly one screen — the post-lock
// scorecard — by instruction (2026-09-21). Every surface before the lock stops
// at the statement.
// ═══════════════════════════════════════════════════════════════════════════

// Two initials, matching the owner avatar on the CEO's board.
const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");

const ObjectiveDetails = ({
  // `[{ sequence, statement }]` — already normalised by the caller.
  keyResults = [],
  ownerName = null,
  // Only used to know that an owner EXISTS when no name came with it. The id
  // itself is never printed: "User #41" is system text (JK copy review O21 —
  // show the owner on every objective, but in words).
  ownerUserId = null,
}) => {
  const krs = keyResults || [];
  if (!krs.length && !ownerName && ownerUserId == null) return null;

  return (
    <div className="okrd">
      {krs.length ? (
        <>
          {/* One quiet label above the measures. It is not a heading competing
              with the Objective statement — it names the group, nothing more.
              A count chip and a "KR 1" badge per row were both tried and both
              removed: three labels on three short sentences is furniture. */}
          <span className="okrd__label">Key Results</span>

          <ul className="okrd__krs">
            {krs.map((kr, index) => (
              <li key={kr.sequence ?? index} className="okrd__kr">
                {/* The number alone — no dot, no prefix, no badge. Falls back
                    to position when `sequence` is missing. */}
                <span className="okrd__kr-seq">{kr.sequence ?? index + 1}</span>
                <span className="okrd__kr-text">{kr.statement || "—"}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* ⚠️ ONE OWNER PER OBJECTIVE, NOT PER KEY RESULT, and that is the data
          model rather than a simplification: an Objective has a single
          `owner_user_id` (§8) and a Key Result carries no owner on any
          endpoint. Rendered once under the set — repeating it per row would
          imply three assignments where there is one. */}
      {/* Always rendered once there is something to show: JK wants the owner
          on every objective, so a missing one is said, not hidden. */}
      {ownerName || ownerUserId != null || krs.length ? (
        <div className="okrd__owner">
          <span className="okrd__owner-avatar">
            {initialsOf(ownerName) || (
              <i className="fa-solid fa-user" aria-hidden="true" />
            )}
          </span>
          <span className="okrd__owner-text">
            {ownerName ? (
              <>
                Owned by <b>{ownerName}</b>
              </>
            ) : ownerUserId != null ? (
              "Owner name not available"
            ) : (
              "No owner set"
            )}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default ObjectiveDetails;
